// The platform broadcast composer offers three intents: save a draft, send
// now, or schedule for later. They map onto two server endpoints that differ
// by blast radius (RBAC Decision C):
//   • draft            → POST /admin/broadcasts/create — low blast radius,
//     no step-up.
//   • send / schedule  → POST /admin/broadcasts — step-up gated, because a
//     platform broadcast can target every member. The server treats a
//     missing scheduledSendAt as "send now" (defaults it to now()); the
//     broadcast dispatcher drains scheduled rows on the next ~1-min tick.
//
// This routing is security-relevant — picking the wrong endpoint either
// bypasses the step-up gate or (as a past bug did) drops a "Send now" into
// the draft endpoint, where it becomes a draft the dispatcher never sends.
// It lives here as a pure, exhaustive function so it stays testable without
// rendering the mutation hook.

import type { components } from '@leanin/api-client';
import type { BroadcastEmailSendVia } from '@leanin/shared/broadcast-enums';
import type { BroadcastDoc } from '../queries/broadcasts';

type AudienceSpec = components['schemas']['AudienceSpec'];

// What the broadcast delivers, by channel — the same closed union the
// server's request bodies discriminate on `sendVia`. A Direct message is
// plain text from the Lean In account; it has no subject, preheader or
// reply-to.
export type BroadcastComposeContent =
  | {
      readonly sendVia: BroadcastEmailSendVia;
      readonly subject: string;
      readonly bodyDoc: BroadcastDoc;
      readonly preheaderText?: string | null;
      readonly replyTo?: string | null;
    }
  | { readonly sendVia: 'dm'; readonly bodyText: string };

// `scheduledSendAt` is only meaningful when scheduling, so it lives on the
// 'schedule' variant alone (invariant #4). This makes the illegal states
// unrepresentable rather than silently coerced: a 'schedule' with no time
// won't typecheck, and a 'send'/'draft' can't carry a stray time.
export type CreateBroadcastInput = {
  readonly audienceSpec: AudienceSpec;
  readonly content: BroadcastComposeContent;
} & ({ readonly mode: 'send' | 'draft' } | { readonly mode: 'schedule'; readonly scheduledSendAt: string });

type CreateBody = components['schemas']['AdminCreateBroadcastRequest'];
type ScheduleBody = components['schemas']['ScheduleAdminBroadcastRequest'];

export type BroadcastCreatePlan =
  | { readonly endpoint: '/api/v1/admin/broadcasts'; readonly body: ScheduleBody }
  | {
      readonly endpoint: '/api/v1/admin/broadcasts/create';
      readonly body: CreateBody;
    };

export function planBroadcastCreate(
  input: CreateBroadcastInput,
): BroadcastCreatePlan {
  const body: CreateBody = { audienceSpec: input.audienceSpec, ...input.content };
  switch (input.mode) {
    case 'send':
      // "Send now" is a schedule with no time: the server defaults
      // scheduled_send_at to now() and the dispatcher sends on the next tick.
      return { endpoint: '/api/v1/admin/broadcasts', body };
    case 'schedule':
      return {
        endpoint: '/api/v1/admin/broadcasts',
        body: { ...body, scheduledSendAt: input.scheduledSendAt },
      };
    case 'draft':
      return { endpoint: '/api/v1/admin/broadcasts/create', body };
  }
}
